"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { ButtonGroup, ButtonGroupItem } from "@/components/ui/button-group"
import {
  FORMATS,
  type FormatKey,
  type Recipe,
  fitFormat,
  recipeLabel,
} from "@/lib/cover-press/engine"

import { SECTION_HELP } from "./help-copy"
import { HelpTip } from "./help-tip"
import { PressCanvas } from "./press-canvas"

/**
 * Zoom is relative to the export: 1 shows the export's own pixels. Below 1
 * the pull is painted at that size (the engine scales its effects, so it is
 * the same picture, smaller). Above 1 the export-size paint is magnified
 * without smoothing, so wear flecks and tooth can be checked pixel by pixel.
 * "Fit" paints to the stage.
 */
export const ZOOMS = [
  { key: "fit", label: "Fit", factor: null },
  { key: "0.5", label: "50%", factor: 0.5 },
  { key: "1", label: "100%", factor: 1 },
  { key: "2", label: "200%", factor: 2 },
] as const

export type ZoomKey = (typeof ZOOMS)[number]["key"]

/** Paints past this many pixels wait for the slider to settle. */
const HEAVY_PX = 900 * 900
const SETTLE_MS = 160
const STAGE_PADDING = 12

/** Short labels for the format switch; the full name lives in its help card. */
const FORMAT_SHORT: Record<FormatKey, string> = {
  cover: "Cover",
  card: "Card",
  story: "Story",
  poster: "Poster",
  cd: "CD",
  back: "Back",
}

interface StageProps {
  recipe: Recipe
  zoom: ZoomKey
  onZoomChange: (zoom: ZoomKey) => void
  onFormatChange: (format: FormatKey) => void
}

/** The proof at the chosen zoom inside a fixed, scrollable frame. */
export function Stage({
  recipe,
  zoom,
  onZoomChange,
  onFormatChange,
}: StageProps) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [fitPx, setFitPx] = useState(520)
  const format = FORMATS[recipe.format]

  // "Fit" tracks the frame. Rounded to 16px so a resize does not cascade
  // into a repaint per pixel.
  useLayoutEffect(() => {
    const el = frameRef.current
    if (!el) return
    const measure = () => {
      const px = Math.min(el.clientWidth, el.clientHeight) - STAGE_PADDING * 2
      setFitPx(Math.max(160, Math.floor(px / 16) * 16))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const factor = ZOOMS.find((z) => z.key === zoom)?.factor ?? null
  const painted =
    factor == null
      ? fitFormat(recipe.format, fitPx)
      : {
          w: Math.round(format.w * Math.min(factor, 1)),
          h: Math.round(format.h * Math.min(factor, 1)),
        }
  const shown =
    factor == null
      ? painted
      : { w: Math.round(format.w * factor), h: Math.round(format.h * factor) }
  const magnified = factor != null && factor > 1
  const heavy = painted.w * painted.h > HEAVY_PX

  // Recentre the frame whenever the shown size changes, so zooming keeps the
  // middle of the pull in view rather than its top-left corner.
  useEffect(() => {
    const el = frameRef.current
    if (!el) return
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2
  }, [shown.w, shown.h])

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div
        ref={frameRef}
        className="bg-secondary ring-secondary relative min-h-0 flex-1 overflow-auto rounded-xl ring-1 ring-inset"
      >
        <div
          className="flex min-h-full min-w-full [align-items:safe_center] [justify-content:safe_center]"
          style={{ padding: STAGE_PADDING }}
        >
          <PressCanvas
            key={`${recipe.format}-${painted.w}x${painted.h}`}
            recipe={recipe}
            width={painted.w}
            height={painted.h}
            post
            delayMs={heavy ? SETTLE_MS : 0}
            role="img"
            aria-label={`${format.label} proof: ${recipeLabel(recipe)}`}
            className={
              format.post
                ? "block shrink-0"
                : "block shrink-0 rounded-md shadow-sm"
            }
            style={{
              width: shown.w,
              height: shown.h,
              imageRendering: magnified ? "pixelated" : "auto",
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <ButtonGroup
              size="sm"
              aria-label="Format"
              selectedKeys={[recipe.format]}
              disallowEmptySelection
              onSelectionChange={(keys) => {
                const [k] = Array.from(keys)
                if (k != null) onFormatChange(k as FormatKey)
              }}
            >
              {(Object.keys(FORMATS) as FormatKey[]).map((k) => (
                <ButtonGroupItem key={k} id={k} aria-label={FORMATS[k].label}>
                  {FORMAT_SHORT[k]}
                </ButtonGroupItem>
              ))}
            </ButtonGroup>
            <HelpTip
              label="Format"
              help={{ what: `${SECTION_HELP.format.what} ${format.note}.` }}
              placement="top"
            />
          </div>
          <div className="flex items-center gap-2">
            <ButtonGroup
              size="sm"
              aria-label="Zoom"
              selectedKeys={[zoom]}
              disallowEmptySelection
              onSelectionChange={(keys) => {
                const [k] = Array.from(keys)
                if (k != null) onZoomChange(k as ZoomKey)
              }}
            >
              {ZOOMS.map((z) => (
                <ButtonGroupItem key={z.key} id={z.key}>
                  {z.label}
                </ButtonGroupItem>
              ))}
            </ButtonGroup>
            <HelpTip label="Zoom" help={SECTION_HELP.zoom} placement="top" />
          </div>
        </div>
        <p className="text-tertiary flex flex-wrap justify-between gap-x-4 text-xs tabular-nums">
          <span>
            {format.label} · {format.w} × {format.h} px · {format.note}
          </span>
          <span>
            painted {painted.w} × {painted.h}
            {magnified ? ` · shown at ${factor}×` : ""}
          </span>
        </p>
      </div>
    </div>
  )
}
